import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiAlertTriangle,
  FiArrowDownRight,
  FiArrowUpRight,
  FiDownload,
  FiCalendar,
  FiChevronDown,
  FiClock,
  FiBriefcase,
  FiFileText,
  FiFilter,
  FiHash,
  FiMapPin,
  FiMoon,
  FiSun,
  FiTrendingUp,
  FiZap,
} from 'react-icons/fi';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import html2pdf from 'html2pdf.js/dist/html2pdf.bundle.min.js';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Api from '../../Api';
import { Button, Card, DatePeriodFilter, MultiSelect, PageComponent, Select, Skeletons, getDateParams, TableActions } from '../../components';
import '../CompanyManager/style.css';
import './style.css';

function formatHHMM(value) {
  if (!value) return '--:--';
  const str = String(value).trim();
  if (!str) return '--:--';
  const sign = str.startsWith('-') ? '-' : '';
  const raw = sign ? str.slice(1) : str;
  const parts = raw.split(':');
  if (parts.length < 2) return str;
  return `${sign}${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
}

function getInitials(name) {
  const safe = String(name || '').trim();
  if (!safe) return '??';
  const parts = safe.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0].slice(0, 1)}${parts[parts.length - 1].slice(0, 1)}`.toUpperCase();
}

function getBalanceMeta(value) {
  const str = String(value || '').trim();
  if (!str) return { className: '', Icon: null };
  if (str.startsWith('-')) return { className: 'timesheet-value-negative', Icon: FiArrowDownRight };
  if (str === '00:00:00') return { className: '', Icon: null };
  return { className: 'timesheet-value-positive', Icon: FiArrowUpRight };
}

function minutesToHHMM(minutes) {
  const m = Math.max(0, Math.round(Number(minutes || 0)));
  const hh = String(Math.floor(m / 60)).padStart(2, '0');
  const mm = String(m % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

function sanitizeSheetName(name) {
  const safe = String(name || 'Planilha')
    .replace(/[\[\]\*\?:\/\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!safe) return 'Planilha';
  return safe.length > 31 ? safe.slice(0, 31) : safe;
}

function formatDate(dateString) {
  if (!dateString || typeof dateString !== 'string') return '-';
  const parts = dateString.split('-');
  if (parts.length !== 3) return dateString; // Retorna o original se não for YYYY-MM-DD
  const [year, month, day] = parts;
  return `${day.split('T')[0]}/${month}/${year}`;
}

async function imageToDataUrl(url) {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        const img = new Image();
        img.onload = () => {
          resolve({
            base64,
            width: img.width,
            height: img.height,
            ratio: img.width / img.height
          });
        };
        img.onerror = () => resolve({ base64, width: 0, height: 0, ratio: 1 });
        img.src = base64;
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error converting image to data URL:', error);
    return null;
  }
}

function DropdownButton({ label, children, variant = 'secondary' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, minWidth: 220 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const updatePosition = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    
    // Pegamos a largura e altura reais do dropdown se ele já estiver no DOM
    const dropdownWidth = dropdownRef.current ? dropdownRef.current.offsetWidth : 240;
    const dropdownHeight = dropdownRef.current ? dropdownRef.current.offsetHeight : 300;
    
    let left = rect.left + window.scrollX;
    let top = rect.bottom + window.scrollY + 8;

    // Se o dropdown for sair pela direita da tela, alinha ele pela direita do botão
    if (left + dropdownWidth > window.innerWidth + window.scrollX) {
      left = rect.right + window.scrollX - dropdownWidth;
    }

    // Se o dropdown for sair por baixo da tela, abre ele para cima do botão
    if (top + dropdownHeight > window.innerHeight + window.scrollY) {
      top = rect.top + window.scrollY - dropdownHeight - 8;
    }

    // Garante que não saia pela esquerda (mínimo 10px da borda)
    left = Math.max(10, left);
    // Garante que não saia pelo topo (mínimo 10px da borda)
    top = Math.max(10, top);

    setPosition({
      top,
      left,
      minWidth: Math.max(220, rect.width),
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    
    // Usamos um pequeno delay para garantir que o DOM do dropdown já foi renderizado
    // e possamos medir o offsetHeight/offsetWidth real.
    const timer = setTimeout(updatePosition, 0);
    
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      const clickedInsideDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      const clickedInsideButton = buttonRef.current && buttonRef.current.contains(event.target);
      if (!clickedInsideDropdown && !clickedInsideButton) setIsOpen(false);
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <>
      <span ref={buttonRef} style={{ display: 'inline-flex' }}>
        <Button
          type="button"
          variant={variant}
          endIcon={<FiChevronDown size={16} />}
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        >
          {label}
        </Button>
      </span>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="table-actions-dropdown"
            style={{ top: position.top, left: position.left, minWidth: position.minWidth, right: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            {React.Children.map(children, (child) => {
              if (React.isValidElement(child)) {
                return React.cloneElement(child, {
                  onClick: (e) => {
                    setIsOpen(false);
                    if (child.props.onClick) child.props.onClick(e);
                  },
                });
              }
              return child;
            })}
          </div>,
          document.body
        )}
    </>
  );
}

export default function Timesheet() {
  const [companies, setCompanies] = useState([]);
  const [companyFilterValues, setCompanyFilterValues] = useState(['all']);

  const [serviceStations, setServiceStations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [positions, setPositions] = useState([]);

  const [selectedServiceStation, setSelectedServiceStation] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('');

  const [currentDate, setCurrentDate] = useState(new Date());
  const [periodMode, setPeriodMode] = useState('monthly');
  const [customPeriod, setCustomPeriod] = useState({ startDate: '', endDate: '' });
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [timesheetLoading, setTimesheetLoading] = useState(false);
  const [timesheetHasLoadedOnce, setTimesheetHasLoadedOnce] = useState(false);
  const [timesheetError, setTimesheetError] = useState('');
  const [timesheetData, setTimesheetData] = useState([]);

  // Virtualization state
  const [visibleCount, setVisibleCount] = useState(20);
  const listRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    Api.getCompanies()
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.data || [];
        setCompanies(list);
      })
      .catch(console.error)
      .finally(() => {
        setHasLoadedOnce(true);
        setLoading(false);
      });
  }, []);

  const selectedCompanyIds = useMemo(() => {
    const allValue = 'all';
    const idsWithoutAll = companyFilterValues.filter((v) => v !== allValue);
    const selectedIds = companyFilterValues.includes(allValue)
      ? companies.map((c) => String(c.id))
      : idsWithoutAll;
    return selectedIds.filter(Boolean);
  }, [companies, companyFilterValues]);

  const selectedCompanyKey = useMemo(() => selectedCompanyIds.join(','), [selectedCompanyIds]);

  useEffect(() => {
    const allValue = 'all';
    const idsWithoutAll = companyFilterValues.filter((v) => v !== allValue);
    const selectedIds = companyFilterValues.includes(allValue)
      ? companies.map((c) => String(c.id))
      : idsWithoutAll;

    if (selectedIds.length === 0) {
      setServiceStations([]);
      setEmployees([]);
      setPositions([]);
      return;
    }

    const toOption = (arr, valueKey = 'id', labelKey = 'name') => {
      if (!Array.isArray(arr)) return [];
      return arr.map((item) => ({
        value: String(item[valueKey]),
        label: item[labelKey] || `#${item[valueKey]}`,
      }));
    };

    const fetchAllCompanies = async (fn) => {
      const results = await Promise.allSettled(selectedIds.map((id) => fn(id)));
      return results
        .filter((r) => r.status === 'fulfilled')
        .flatMap((r) => (Array.isArray(r.value) ? r.value : r.value?.data || []));
    };

    fetchAllCompanies((id) => Api.getServiceStations(id))
      .then((list) => setServiceStations(toOption(list)))
      .catch(console.error);

    fetchAllCompanies((id) => Api.getEmployees(id))
      .then((list) => setEmployees(toOption(list)))
      .catch(console.error);

    fetchAllCompanies((id) => Api.getPositions(id))
      .then((list) => setPositions(toOption(list, 'id', 'name')))
      .catch(console.error);
  }, [companies, companyFilterValues]);

  const companyOptions = useMemo(() => {
    const base = [{ value: 'all', label: 'TODAS' }];
    const list = companies.map((c) => ({
      value: String(c.id),
      label:
        c.trade_name ||
        c.corporate_name ||
        c.name ||
        `Empresa ${c.id}`,
    }));
    return [...base, ...list];
  }, [companies]);

  const stationOptions = useMemo(() => {
    const unique = new Map();
    serviceStations.forEach((s) => unique.set(String(s.value), s));
    return Array.from(unique.values()).sort((a, b) =>
      String(a.label || '').localeCompare(String(b.label || ''))
    );
  }, [serviceStations]);

  const employeeOptions = useMemo(() => {
    const unique = new Map();
    employees.forEach((e) => unique.set(String(e.value), e));
    return Array.from(unique.values()).sort((a, b) =>
      String(a.label || '').localeCompare(String(b.label || ''))
    );
  }, [employees]);

  const positionOptions = useMemo(() => {
    const unique = new Map();
    positions.forEach((p) => {
      const label = String(p.label || '').trim();
      if (!label) return;
      const key = label.toLowerCase();
      if (!unique.has(key)) {
        unique.set(key, {
          value: key,
          label: label,
        });
      }
    });
    return Array.from(unique.values()).sort((a, b) =>
      String(a.label || '').localeCompare(String(b.label || ''))
    );
  }, [positions]);

  const filteredTimesheetData = useMemo(() => {
    let data = timesheetData || [];
    if (selectedPosition) {
      const wanted = String(selectedPosition).toLowerCase();
      data = data.filter((row) => {
        const name = row?.employee?.position?.name || '';
        return String(name).toLowerCase() === wanted;
      });
    }
    return data;
  }, [selectedPosition, timesheetData]);

  // Virtualization effect: Infinite scroll style for the list
  useEffect(() => {
    const handleScroll = () => {
      if (!listRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      if (scrollTop + clientHeight >= scrollHeight - 500) {
        setVisibleCount((prev) => Math.min(prev + 20, filteredTimesheetData.length));
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [filteredTimesheetData.length]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(20);
  }, [selectedPosition, selectedEmployee, selectedServiceStation, selectedCompanyKey, periodMode, currentDate]);

  const buildMirrorRows = React.useCallback((source) => {
    const rows = [];
    (source || []).forEach((row) => {
      const emp = row.employee || {};
      (row.days || []).forEach((d) => {
        rows.push({
          Empresa: emp.company?.name || '',
          Colaborador: emp.name || '',
          Matrícula: emp.registration_number || '',
          Data: d.date,
          Batidas: Array.isArray(d.punches) ? d.punches.join(' | ') : '',
          'Horas Trabalhadas': minutesToHHMM(d.workedMinutes),
          'Horas Previstas': minutesToHHMM(d.expectedMinutes),
          Saldo: d.balanceMinutes != null ? `${d.balanceMinutes < 0 ? '-' : ''}${minutesToHHMM(Math.abs(d.balanceMinutes))}` : '',
          'Falta Justificada': d.justifiedAbsence ? 'Sim' : 'Não',
          Override: d.override || '',
        });
      });
    });
    return rows;
  }, []);

  const runInWebWorker = (data, fileName, sheetName) => {
    return new Promise((resolve, reject) => {
      const workerCode = `
        importScripts('https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js');
        self.onmessage = function(e) {
          try {
            const { data, fileName, sheetName } = e.data;
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, sheetName);
            const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            self.postMessage({ buffer: wbout, fileName }, [wbout]);
          } catch (err) {
            self.postMessage({ error: err.message });
          }
        };
      `;
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      const worker = new Worker(URL.createObjectURL(blob));

      worker.onmessage = (e) => {
        if (e.data.error) {
          reject(new Error(e.data.error));
        } else {
          const { buffer, fileName } = e.data;
          const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          a.click();
          URL.revokeObjectURL(url);
          resolve();
        }
        worker.terminate();
      };

      worker.onerror = (err) => {
        reject(err);
        worker.terminate();
      };

      worker.postMessage({ data, fileName, sheetName });
    });
  };

  async function handleExportFiscal(format) {
    if (!filteredTimesheetData || filteredTimesheetData.length === 0) {
      toast.warning('Não há dados para exportar com os filtros atuais.');
      return;
    }

    const dp = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);
    const start = formatDate(dp.startDate).replace(/\//g, '');
    const end = formatDate(dp.endDate).replace(/\//g, '');
    
    // Helper para preenchimento de campos fixos
    const pad = (str, length, char = ' ', right = false) => {
      const s = String(str || '').substring(0, length);
      return right ? s.padEnd(length, char) : s.padStart(length, char);
    };

    let content = '';
    let filename = '';

    try {
      if (format === 'afd_1510' || format === 'afd_671') {
        filename = `AFD_${start}_${end}.txt`;
        // Registro Tipo 1 - Cabeçalho
        const firstRow = filteredTimesheetData[0];
        const company = firstRow.employee?.company || {};
        const cnpj = company.cnpj?.replace(/\D/g, '') || '';
        const cei = pad('', 12, '0');
        const razaoSocial = pad(company.corporate_name || company.name, 150, ' ', true);
        const numFabricacao = pad('00000000000000000', 17, '0');
        const dataInicio = start;
        const dataFim = end;
        const dataGeracao = new Date().toLocaleDateString('pt-BR').replace(/\//g, '');
        const horaGeracao = new Date().toLocaleTimeString('pt-BR', { hour12: false }).replace(/\D/g, '').substring(0, 4);

        content += `0000000001${pad(cnpj, 14, '0')}${cei}${razaoSocial}${numFabricacao}${dataInicio}${dataFim}${dataGeracao}${horaGeracao}\r\n`;

        let seq = 2;
        filteredTimesheetData.forEach(row => {
          const emp = row.employee || {};
          const pis = emp.pis_pasep?.replace(/\D/g, '') || '';
          if (!pis) return;

          (row.days || []).forEach(day => {
            const dateStr = day.date.replace(/\D/g, '').substring(0, 8); // YYYYMMDD -> DDMMYYYY
            const formattedDate = dateStr.substring(6, 8) + dateStr.substring(4, 6) + dateStr.substring(0, 4);
            
            (day.punches || []).forEach(punch => {
              const timeStr = punch.replace(/\D/g, '');
              // Registro Tipo 3 - Marcação de Ponto
              content += `${pad(seq, 9, '0')}3${formattedDate}${timeStr}${pad(pis, 12, '0')}\r\n`;
              seq++;
            });
          });
        });

        // Registro Tipo 9 - Trailer
        content += `${pad(seq, 9, '0')}9${pad('', 14, ' ')}${pad('', 12, ' ')}${pad('', 150, ' ')}${pad(seq, 9, '0')}\r\n`;
      } 
      else if (format === 'afdt') {
        filename = `AFDT_${start}_${end}.txt`;
        let seq = 1;
        filteredTimesheetData.forEach(row => {
          const emp = row.employee || {};
          const pis = emp.pis_pasep?.replace(/\D/g, '') || '';
          const name = pad(emp.name, 52, ' ', true);
          
          (row.days || []).forEach(day => {
            const dateStr = day.date.replace(/\D/g, '').substring(0, 8);
            const formattedDate = dateStr.substring(6, 8) + dateStr.substring(4, 6) + dateStr.substring(0, 4);
            
            (day.punches || []).forEach((punch, idx) => {
              const timeStr = punch.replace(/\D/g, '');
              content += `${pad(seq, 9, '0')}${formattedDate}${timeStr}${pad(pis, 12, '0')}${pad(idx + 1, 2, '0')}${name}O\r\n`;
              seq++;
            });
          });
        });
      }
      else if (format === 'acjef') {
        filename = `ACJEF_${start}_${end}.txt`;
        const firstRow = filteredTimesheetData[0];
        const company = firstRow.employee?.company || {};
        
        // Header
        content += `00000000011${pad(company.cnpj?.replace(/\D/g, ''), 14, '0')}${pad('', 12, '0')}${pad(company.corporate_name || company.name, 150, ' ', true)}${start}${end}${new Date().toLocaleDateString('pt-BR').replace(/\//g, '')}\r\n`;
        
        let seq = 2;
        filteredTimesheetData.forEach(row => {
          const emp = row.employee || {};
          const pis = emp.pis_pasep?.replace(/\D/g, '') || '';
          if (!pis) return;

          (row.days || []).forEach(day => {
            const dateStr = day.date.replace(/\D/g, '').substring(0, 8);
            const formattedDate = dateStr.substring(6, 8) + dateStr.substring(4, 6) + dateStr.substring(0, 4);
            const worked = pad(minutesToHHMM(day.workedMinutes).replace(/\D/g, ''), 4, '0');
            const expected = pad(minutesToHHMM(day.expectedMinutes).replace(/\D/g, ''), 4, '0');
            
            // Registro Tipo 3 - Resumo Diário
            content += `${pad(seq, 9, '0')}3${pad(pis, 12, '0')}${formattedDate}${expected}${worked}${pad('0000', 4, '0')}${pad('0000', 4, '0')}00\r\n`;
            seq++;
          });
        });
      }

      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Arquivo gerado com sucesso!');
    } catch (err) {
      console.error(err);
      toast.error('Erro ao gerar arquivo fiscal.');
    }
  }

  async function handleExport(type, dataOverride = null, filenamePrefixOverride = null) {
    const source = Array.isArray(dataOverride) ? dataOverride : filteredTimesheetData;
    if (!source || source.length === 0) {
      toast.warning('Não há dados para exportar com os filtros atuais.');
      return;
    }

    const dp = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);
    const start = dp.startDate || '';
    const end = dp.endDate || '';
    const prefix = filenamePrefixOverride || 'folha_de_ponto';

    const toastId = toast.info('Preparando exportação...', { autoClose: false });

    try {
      if (type === 'summary') {
        const exportData = source.map((row) => {
          const emp = row.employee || {};
          const totals = row.totals || {};
          return {
            Empresa: emp.company?.name || '',
            Colaborador: emp.name || '',
            Matrícula: emp.registration_number || '',
            ID: emp.id || '',
            Admissão: emp.admission_date || '',
            Cargo: emp.position?.name || '',
            Jornada: emp.journey?.name || '',
            'Dias Trabalhados': totals.daysWorked ?? 0,
            'Hrs. Trabalhadas': formatHHMM(totals.hoursWorked),
            'Hrs. Projeção': formatHHMM(totals.projectedHours),
            'HE. Diurno': formatHHMM(totals.overtimeDay),
            'HE. Noturno': formatHHMM(totals.overtimeNight),
            'Hrs. Intra': formatHHMM(totals.hoursIntra),
            'Hrs. Escala': formatHHMM(totals.hoursScale),
            'Add. Noturno': formatHHMM(totals.nightAdditionalHours),
            'Saldo Anterior': formatHHMM(totals.bankPrevious),
            'Saldo Mês': formatHHMM(totals.bankMonth),
            Acumulado: formatHHMM(totals.bankAccumulated),
            Faltas: totals.absencesTotal ?? 0,
            'Faltas Justificadas': totals.absencesJustified ?? 0,
            'Faltas Injustificadas': totals.absencesUnjustified ?? 0,
          };
        });

        await runInWebWorker(exportData, `${prefix}_resumo_${start}_${end}.xlsx`, 'Resumo');
      } else if (type === 'mirror') {
        const rows = buildMirrorRows(source);
        await runInWebWorker(rows, `${prefix}_espelho_${start}_${end}.xlsx`, 'Espelho');
      } else if (type === 'audit') {
        const rows = [];
        source.forEach((row) => {
          const emp = row.employee || {};
          (row.days || []).forEach((d) => {
            rows.push({
              Empresa: emp.company?.name || '',
              Colaborador: emp.name || '',
              Matrícula: emp.registration_number || '',
              Data: d.date,
              'Horas Previstas (min)': d.expectedMinutes ?? 0,
              'Horas Trabalhadas (min)': d.workedMinutes ?? 0,
              'Saldo (min)': d.balanceMinutes ?? 0,
              Batidas: Array.isArray(d.punches) ? d.punches.join(' | ') : '',
              'Falta Justificada': d.justifiedAbsence ? 'Sim' : 'Não',
              Override: d.override || '',
            });
          });
        });
        await runInWebWorker(rows, `${prefix}_apuracao_${start}_${end}.xlsx`, 'Apuração');
      } else if (type === 'detailed') {
        const rows = [];
        source.forEach((row) => {
          const emp = row.employee || {};
          const totals = row.totals || {};
          (row.days || []).forEach((d) => {
            rows.push({
              Empresa: emp.company?.name || '',
              Colaborador: emp.name || '',
              Matrícula: emp.registration_number || '',
              Cargo: emp.position?.name || '',
              Data: d.date,
              Batidas: Array.isArray(d.punches) ? d.punches.join(' | ') : '',
              'Horas Trabalhadas': minutesToHHMM(d.workedMinutes),
              'Horas Previstas': minutesToHHMM(d.expectedMinutes),
              Saldo: d.balanceMinutes != null ? `${d.balanceMinutes < 0 ? '-' : ''}${minutesToHHMM(Math.abs(d.balanceMinutes))}` : '',
              'Add. Noturno (Total período)': formatHHMM(totals.nightAdditionalHours),
              'Saldo Mês (Total período)': formatHHMM(totals.bankMonth),
              'Falta Justificada': d.justifiedAbsence ? 'Sim' : 'Não',
              Override: d.override || '',
            });
          });
        });
        await runInWebWorker(rows, `${prefix}_detalhado_${start}_${end}.xlsx`, 'Detalhado');
      } else if (type === 'night_additional') {
        const exportData = source.map((row) => {
          const emp = row.employee || {};
          const totals = row.totals || {};
          return {
            Empresa: emp.company?.name || '',
            Colaborador: emp.name || '',
            Matrícula: emp.registration_number || '',
            Cargo: emp.position?.name || '',
            'Add. Noturno': formatHHMM(totals.nightAdditionalHours),
            'HE. Noturno': formatHHMM(totals.overtimeNight),
            'Hrs. Trabalhadas': formatHHMM(totals.hoursWorked),
            'Hrs. Projeção': formatHHMM(totals.projectedHours),
          };
        });
        await runInWebWorker(exportData, `${prefix}_add_noturno_${start}_${end}.xlsx`, 'Adicional Noturno');
      } else if (type === 'bank_hours') {
        const exportData = source.map((row) => {
          const emp = row.employee || {};
          const totals = row.totals || {};
          return {
            Empresa: emp.company?.name || '',
            Colaborador: emp.name || '',
            Matrícula: emp.registration_number || '',
            Cargo: emp.position?.name || '',
            'Saldo Anterior': formatHHMM(totals.bankPrevious),
            'Saldo Mês': formatHHMM(totals.bankMonth),
            Acumulado: formatHHMM(totals.bankAccumulated),
          };
        });
        await runInWebWorker(exportData, `${prefix}_banco_de_horas_${start}_${end}.xlsx`, 'Banco de Horas');
      } else if (type === 'absences') {
        const rows = [];
        source.forEach((row) => {
          const emp = row.employee || {};
          (row.days || []).forEach((d) => {
            const expected = Number(d.expectedMinutes || 0);
            const worked = Number(d.workedMinutes || 0);
            if (expected > 0 && worked === 0) {
              rows.push({
                Empresa: emp.company?.name || '',
                Colaborador: emp.name || '',
                Matrícula: emp.registration_number || '',
                Cargo: emp.position?.name || '',
                Data: d.date,
                'Justificada': d.justifiedAbsence ? 'Sim' : 'Não',
                Override: d.override || '',
              });
            }
          });
        });
        await runInWebWorker(rows, `${prefix}_faltas_${start}_${end}.xlsx`, 'Faltas');
      }
      toast.dismiss(toastId);
      toast.success('Exportação concluída com sucesso!');
    } catch (err) {
      console.error(err);
      toast.dismiss(toastId);
      toast.error('Erro ao processar exportação.');
    }
  }

  async function handleMirrorByCargo() {
    const dp = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);
    const start = dp.startDate || '';
    const end = dp.endDate || '';

    if (!filteredTimesheetData || filteredTimesheetData.length === 0) {
      toast.warning('Não há dados para exportar com os filtros atuais.');
      return;
    }

    const toastId = toast.info('Preparando exportação por cargo...', { autoClose: false });

    try {
      const groups = new Map();
      filteredTimesheetData.forEach((row) => {
        const key = row?.employee?.position?.name || 'Sem cargo';
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(row);
      });

      const wb = XLSX.utils.book_new();
      for (const [key, rows] of groups.entries()) {
        const mirrorRows = buildMirrorRows(rows);
        const sheet = XLSX.utils.json_to_sheet(mirrorRows);
        XLSX.utils.book_append_sheet(wb, sheet, sanitizeSheetName(key));
      }

      XLSX.writeFile(wb, `espelho_de_ponto_por_cargo_${start}_${end}.xlsx`);
      toast.dismiss(toastId);
      toast.success('Exportação concluída!');
    } catch (err) {
      console.error(err);
      toast.dismiss(toastId);
      toast.error('Erro na exportação por cargo.');
    }
  }

  async function handleMirrorByPosto() {
    const dp = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);
    const start = dp.startDate || '';
    const end = dp.endDate || '';

    if (!filteredTimesheetData || filteredTimesheetData.length === 0) {
      toast.warning('Não há dados para exportar com os filtros atuais.');
      return;
    }

    const toastId = toast.info('Preparando exportação por posto...', { autoClose: false });

    try {
      const stationNames = new Set();
      filteredTimesheetData.forEach((row) => {
        const stations = row?.employee?.serviceStations || [];
        stations.forEach((s) => {
          if (s && s.name) stationNames.add(String(s.name));
        });
      });

      const wb = XLSX.utils.book_new();

      if (stationNames.size === 0) {
        const sheet = XLSX.utils.json_to_sheet(buildMirrorRows(filteredTimesheetData));
        XLSX.utils.book_append_sheet(wb, sheet, sanitizeSheetName('Sem posto'));
      } else {
        for (const stationName of stationNames) {
          const rows = filteredTimesheetData.filter((row) => {
            const stations = row?.employee?.serviceStations || [];
            return stations.some((s) => String(s?.name || '') === String(stationName));
          });
          if (rows.length > 0) {
            const sheet = XLSX.utils.json_to_sheet(buildMirrorRows(rows));
            XLSX.utils.book_append_sheet(wb, sheet, sanitizeSheetName(stationName));
          }
        }
      }

      XLSX.writeFile(wb, `espelho_de_ponto_por_posto_${start}_${end}.xlsx`);
      toast.dismiss(toastId);
      toast.success('Exportação concluída!');
    } catch (err) {
      console.error(err);
      toast.dismiss(toastId);
      toast.error('Erro na exportação por posto.');
    }
  }

  async function handleExportAllPDF() {
    if (!filteredTimesheetData || filteredTimesheetData.length === 0) {
      toast.warning('Não há dados para exportar com os filtros atuais.');
      return;
    }

    const toastId = toast.info('Gerando documentos (isso será ultra rápido agora)...', { autoClose: false });

    try {
      const dp = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);
      const start = formatDate(dp.startDate);
      const end = formatDate(dp.endDate);

      // 1. Pré-carregar logos
      const imageCache = new Map();
      const systemLogoUrl = `${window.location.origin}/logo.png`;
      const systemLogoData = await imageToDataUrl(systemLogoUrl);
      if (systemLogoData) imageCache.set('system_logo', systemLogoData);

      const logoPromises = filteredTimesheetData.map(async (row) => {
        const company = row.employee?.company || {};
        const companyId = String(company.id);
        if (imageCache.has(`logo_${companyId}`)) return;

        const fullCompany = companies.find(c => String(c.id) === companyId) || {};
        const logoData = fullCompany.logo || company.logo;
        if (!logoData) return;

        if (typeof logoData === 'string' && (logoData.startsWith('http') || logoData.startsWith('/'))) {
          const data = await imageToDataUrl(logoData);
          if (data) imageCache.set(`logo_${companyId}`, data);
        } else if (logoData.type === 'Buffer' && Array.isArray(logoData.data)) {
          const base64String = btoa(new Uint8Array(logoData.data).reduce((data, byte) => data + String.fromCharCode(byte), ''));
          const base64 = `data:image/png;base64,${base64String}`;
          const img = new Image();
          await new Promise(r => {
            img.onload = () => {
              imageCache.set(`logo_${companyId}`, { base64, ratio: img.width / img.height });
              r();
            };
            img.onerror = () => {
              imageCache.set(`logo_${companyId}`, { base64, ratio: 1 });
              r();
            };
            img.src = base64;
          });
        } else if (typeof logoData === 'string' && logoData.startsWith('data:image')) {
          const base64 = logoData;
          const img = new Image();
          await new Promise(r => {
            img.onload = () => {
              imageCache.set(`logo_${companyId}`, { base64, ratio: img.width / img.height });
              r();
            };
            img.onerror = () => {
              imageCache.set(`logo_${companyId}`, { base64, ratio: 1 });
              r();
            };
            img.src = base64;
          });
        }
      });

      await Promise.all(logoPromises);

      // 2. Criar o documento PDF (A4 em mm)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      // 3. Processar cada funcionário (ultra rápido com jsPDF nativo)
      for (let i = 0; i < filteredTimesheetData.length; i++) {
        if (i % 20 === 0) {
          toast.update(toastId, { render: `Gerando página ${i + 1} de ${filteredTimesheetData.length}...` });
        }
        if (i > 0) doc.addPage();
        
        const row = filteredTimesheetData[i];
        const emp = row.employee || {};
        const company = emp.company || {};
        const totals = row.totals || {};
        const days = row.days || [];
        const companyId = String(company.id);
        const logoSrc = imageCache.get(`logo_${companyId}`);
        const systemLogoSrc = imageCache.get('system_logo');

        // --- Cabeçalho ---
        // Logo
        let logoEndX = 10; // Ponto final da logo no eixo X para calcular o início do texto
        if (logoSrc) {
          try {
            const maxWidth = 42;
            const maxHeight = 16;
            let finalWidth = maxWidth;
            let finalHeight = maxWidth / (logoSrc.ratio || 1);
            
            if (finalHeight > maxHeight) {
              finalHeight = maxHeight;
              finalWidth = maxHeight * (logoSrc.ratio || 1);
            }
            
            const yPos = 5 + (20 - finalHeight) / 2;
            doc.addImage(logoSrc.base64, 'PNG', 10, yPos, finalWidth, finalHeight, undefined, 'FAST');
            logoEndX = 10 + finalWidth + 5; // Logo finaliza em x=10+largura, adicionamos 5mm de respiro
          } catch (e) {
            logoEndX = 10;
          }
        } else {
          logoEndX = 10;
        }
        
        // Empresa Info - Posicionado dinamicamente após a logo
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text((company.corporate_name || company.name || 'EMPRESA').toUpperCase(), logoEndX, 12);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(`CNPJ: ${company.cnpj || '-'}`, logoEndX, 17);
        doc.text(`${company.address_city || ''}/${company.address_state || ''}`, logoEndX, 21);

        // Título / Período
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('FOLHA DE PONTO', 200, 14, { align: 'right' });
        doc.setFontSize(9);
        doc.text(`Período: ${start} a ${end}`, 200, 19, { align: 'right' });
        
        // Linha divisória
        doc.setDrawColor(0);
        doc.setLineWidth(0.3);
        doc.line(10, 25, 200, 25);

        // --- Info Colaborador (Grid) ---
        doc.setFillColor(245, 245, 245);
        doc.rect(10, 28, 190, 15, 'F');
        doc.setDrawColor(204);
        doc.rect(10, 28, 190, 15, 'S');
        
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.text('Colaborador:', 12, 32);
        doc.text('Matrícula:', 75, 32);
        doc.text('CPF:', 138, 32);
        doc.text('Cargo:', 12, 39);
        doc.text('PIS:', 75, 39);
        doc.text('Admissão:', 138, 39);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text(emp.name || '-', 12, 35);
        doc.text(emp.registration_number || '-', 75, 35);
        doc.text(emp.cpf || '-', 138, 35);
        doc.text(emp.position?.name || '-', 12, 42);
        doc.text(emp.pis_pasep || '-', 75, 42);
        doc.text(formatDate(emp.admission_date), 138, 42);

        // --- Tabela de Pontos ---
        const tableBody = days.map(d => [
          formatDate(d.date),
          Array.isArray(d.punches) ? d.punches.join(' | ') : '-',
          minutesToHHMM(d.expectedMinutes),
          minutesToHHMM(d.workedMinutes),
          { 
            content: d.balanceMinutes != null ? `${d.balanceMinutes < 0 ? '-' : ''}${minutesToHHMM(Math.abs(d.balanceMinutes))}` : '-',
            styles: { textColor: d.balanceMinutes < 0 ? [187, 0, 0] : [0, 102, 0], fontStyle: 'bold' }
          },
          (d.justifiedAbsence ? 'FALTA JUST.' : '') + (d.override ? (d.justifiedAbsence ? ' | ' : '') + d.override : '')
        ]);

        autoTable(doc, {
          startY: 46,
          head: [['Data', 'Registros de Ponto', 'Prev.', 'Trab.', 'Saldo', 'Obs.']],
          body: tableBody,
          theme: 'grid',
          styles: { fontSize: 7, cellPadding: 1, textColor: [0, 0, 0], lineColor: [204, 204, 204] },
          headStyles: { fillColor: [224, 224, 224], textColor: [0, 0, 0], fontStyle: 'bold', lineWidth: 0.1, lineColor: [0, 0, 0] },
          columnStyles: {
            0: { cellWidth: 20, halign: 'center' },
            1: { cellWidth: 80, fontStyle: 'bold' }, // Ajustado para centralizar melhor a tabela
            2: { cellWidth: 15, halign: 'center' },
            3: { cellWidth: 15, halign: 'center' },
            4: { cellWidth: 15, halign: 'center' },
            5: { cellWidth: 45, fontSize: 6.5 }  // Ajustado para centralizar melhor a tabela
          },
          margin: { left: 10, right: 10 },
          tableWidth: 190,
          didDrawPage: (data) => {
            // Se a tabela quebrar página, desenha o cabeçalho novamente? 
            // Para folha de ponto geralmente cabe em uma página, mas se não couber, autoTable lida com o cabeçalho.
          }
        });

        // --- Rodapé (Resumo e Assinaturas) ---
        let finalY = doc.lastAutoTable.finalY + 5;
        if (finalY > 230) { doc.addPage(); finalY = 20; }

        // Resumo e Banco de Horas
        doc.setDrawColor(0);
        doc.setLineWidth(0.1);
        
        // Resumo Período
        doc.rect(10, finalY, 110, 20);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('RESUMO DO PERÍODO', 12, finalY + 4);
        doc.line(10, finalY + 6, 120, finalY + 6);
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.text(`Dias Trab: ${totals.daysWorked ?? 0}`, 12, finalY + 10);
        doc.text(`Horas Trab: ${formatHHMM(totals.hoursWorked)}`, 12, finalY + 14);
        doc.text(`Extras 50%: ${formatHHMM(totals.overtimeDay)}`, 12, finalY + 18);
        doc.text(`Extras 100%: ${formatHHMM(totals.overtimeNight)}`, 65, finalY + 10);
        doc.text(`Add. Noturno: ${formatHHMM(totals.nightAdditionalHours)}`, 65, finalY + 14);
        doc.text(`Faltas: ${totals.absencesTotal ?? 0}`, 65, finalY + 18);

        // Banco de Horas
        doc.rect(125, finalY, 75, 20);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text('BANCO DE HORAS', 127, finalY + 4);
        doc.line(125, finalY + 6, 200, finalY + 6);
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.text(`Saldo Anterior: ${formatHHMM(totals.bankPrevious)}`, 127, finalY + 10);
        doc.text(`Saldo do Mês: ${formatHHMM(totals.bankMonth)}`, 127, finalY + 14);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text(`Saldo Acumulado: ${formatHHMM(totals.bankAccumulated)}`, 127, finalY + 18);

        // Assinaturas
        const sigY = finalY + 45;
        doc.setLineWidth(0.3);
        doc.line(20, sigY, 90, sigY);
        doc.line(120, sigY, 190, sigY);
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text(emp.name || '-', 55, sigY + 4, { align: 'center' });
        doc.text(company.corporate_name || company.name || 'EMPRESA', 155, sigY + 4, { align: 'center' });
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.text('ASSINATURA DO COLABORADOR', 55, sigY + 7, { align: 'center' });
        doc.text('ASSINATURA DO EMPREGADOR', 155, sigY + 7, { align: 'center' });

        // Footer System (Logo e Texto lado a lado)
        const footerText = `Gerado em ${new Date().toLocaleString('pt-BR')} por ClockFy`;
        doc.setFontSize(6);
        const footerTextWidth = doc.getTextWidth(footerText);
        const footerGap = 2; // Espaço entre logo e texto
        
        let footerTotalWidth = footerTextWidth;
        let sysWidth = 0;
        let sysHeight = 0;

        if (systemLogoSrc) {
          const sysMaxWidth = 18;
          const sysMaxHeight = 5;
          sysWidth = sysMaxWidth;
          sysHeight = sysMaxWidth / (systemLogoSrc.ratio || 1);
          if (sysHeight > sysMaxHeight) {
            sysHeight = sysMaxHeight;
            sysWidth = sysMaxHeight * (systemLogoSrc.ratio || 1);
          }
          footerTotalWidth += sysWidth + footerGap;
        }

        const footerStartX = 105 - (footerTotalWidth / 2);
        const footerY = 285; // Movido para o final da folha A4 (297mm)

        if (systemLogoSrc) {
          try {
            // Desenha a logo
            doc.addImage(systemLogoSrc.base64, 'PNG', footerStartX, footerY - (sysHeight / 2) - 0.5, sysWidth, sysHeight, undefined, 'FAST');
            // Desenha o texto ao lado
            doc.setTextColor(100);
            doc.text(footerText, footerStartX + sysWidth + footerGap, footerY, { align: 'left' });
          } catch (e) {
            doc.setTextColor(100);
            doc.text(footerText, 105, footerY, { align: 'center' });
          }
        } else {
          doc.setTextColor(100);
          doc.text(footerText, 105, footerY, { align: 'center' });
        }
        doc.setTextColor(0);
      }

      // 4. Salvar o PDF
      doc.save(`folhas_ponto_${start}_${end}.pdf`.replace(/\//g, '-'));

      toast.dismiss(toastId);
      toast.success('PDF gerado com sucesso!');
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast.dismiss(toastId);
      toast.error('Erro ao gerar PDF em massa.');
    }
  }

  async function handleExportPDF(row) {
    if (!row) return;

    const emp = row.employee || {};
    const company = emp.company || {};
    const totals = row.totals || {};
    const days = row.days || [];

    const dp = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);
    const start = formatDate(dp.startDate);
    const end = formatDate(dp.endDate);

    const toastId = toast.info('Gerando PDF...');

    try {
      // 1. Logos
      const systemLogoUrl = `${window.location.origin}/logo.png`;
      const systemLogo = await imageToDataUrl(systemLogoUrl);

      const companyId = String(company.id);
      const fullCompany = companies.find(c => String(c.id) === companyId) || {};
      const logoData = fullCompany.logo || company.logo;

      let logoSrc = null;
      if (logoData) {
        if (logoData.type === 'Buffer' && Array.isArray(logoData.data)) {
          const base64String = btoa(new Uint8Array(logoData.data).reduce((data, byte) => data + String.fromCharCode(byte), ''));
          const base64 = `data:image/png;base64,${base64String}`;
          const img = new Image();
          await new Promise(r => {
            img.onload = () => {
              logoSrc = { base64, ratio: img.width / img.height };
              r();
            };
            img.onerror = () => {
              logoSrc = { base64, ratio: 1 };
              r();
            };
            img.src = base64;
          });
        } else if (typeof logoData === 'string' && (logoData.startsWith('http') || logoData.startsWith('/'))) {
          logoSrc = await imageToDataUrl(logoData);
        } else if (typeof logoData === 'string' && logoData.startsWith('data:image')) {
          const base64 = logoData;
          const img = new Image();
          await new Promise(r => {
            img.onload = () => {
              logoSrc = { base64, ratio: img.width / img.height };
              r();
            };
            img.onerror = () => {
              logoSrc = { base64, ratio: 1 };
              r();
            };
            img.src = base64;
          });
        }
      }

      // 2. Criar PDF
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });

      // --- Cabeçalho ---
      let logoEndX = 10;
      if (logoSrc) {
        try {
          const maxWidth = 42;
          const maxHeight = 16;
          let finalWidth = maxWidth;
          let finalHeight = maxWidth / (logoSrc.ratio || 1);
          
          if (finalHeight > maxHeight) {
            finalHeight = maxHeight;
            finalWidth = maxHeight * (logoSrc.ratio || 1);
          }
          const yPos = 5 + (20 - finalHeight) / 2;
          doc.addImage(logoSrc.base64, 'PNG', 10, yPos, finalWidth, finalHeight, undefined, 'FAST'); 
          logoEndX = 10 + finalWidth + 5;
        } catch (e) {
          logoEndX = 10;
        }
      } else {
        logoEndX = 10;
      }
      
      // Empresa Info - Posicionado dinamicamente
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text((company.corporate_name || company.name || 'EMPRESA').toUpperCase(), logoEndX, 12);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`CNPJ: ${company.cnpj || '-'}`, logoEndX, 17);
      doc.text(`${company.address_city || ''}/${company.address_state || ''}`, logoEndX, 21);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('FOLHA DE PONTO', 200, 14, { align: 'right' });
      doc.setFontSize(9);
      doc.text(`Período: ${start} a ${end}`, 200, 19, { align: 'right' });
      doc.line(10, 25, 200, 25);

      // --- Info Colaborador ---
      doc.setFillColor(245, 245, 245);
      doc.rect(10, 28, 190, 15, 'F');
      doc.setDrawColor(204);
      doc.rect(10, 28, 190, 15, 'S');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('Colaborador:', 12, 32);
      doc.text('Matrícula:', 75, 32);
      doc.text('CPF:', 138, 32);
      doc.text('Cargo:', 12, 39);
      doc.text('PIS:', 75, 39);
      doc.text('Admissão:', 138, 39);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(emp.name || '-', 12, 35);
      doc.text(emp.registration_number || '-', 75, 35);
      doc.text(emp.cpf || '-', 138, 35);
      doc.text(emp.position?.name || '-', 12, 42);
      doc.text(emp.pis_pasep || '-', 75, 42);
      doc.text(formatDate(emp.admission_date), 138, 42);

      // --- Tabela ---
      const tableBody = days.map(d => [
        formatDate(d.date),
        Array.isArray(d.punches) ? d.punches.join(' | ') : '-',
        minutesToHHMM(d.expectedMinutes),
        minutesToHHMM(d.workedMinutes),
        { 
          content: d.balanceMinutes != null ? `${d.balanceMinutes < 0 ? '-' : ''}${minutesToHHMM(Math.abs(d.balanceMinutes))}` : '-',
          styles: { textColor: d.balanceMinutes < 0 ? [187, 0, 0] : [0, 102, 0], fontStyle: 'bold' }
        },
        (d.justifiedAbsence ? 'FALTA JUST.' : '') + (d.override ? (d.justifiedAbsence ? ' | ' : '') + d.override : '')
      ]);

      autoTable(doc, {
        startY: 46,
        head: [['Data', 'Registros de Ponto', 'Prev.', 'Trab.', 'Saldo', 'Obs.']],
        body: tableBody,
        theme: 'grid',
        styles: { fontSize: 7, cellPadding: 1, textColor: [0, 0, 0] },
        headStyles: { fillColor: [224, 224, 224], textColor: [0, 0, 0], fontStyle: 'bold' },
        columnStyles: { 
          0: { cellWidth: 20, halign: 'center' }, 
          1: { cellWidth: 80, fontStyle: 'bold' }, 
          2: { cellWidth: 15, halign: 'center' }, 
          3: { cellWidth: 15, halign: 'center' }, 
          4: { cellWidth: 15, halign: 'center' }, 
          5: { cellWidth: 45 } 
        },
        margin: { left: 10, right: 10 },
        tableWidth: 190
      });

      // --- Rodapé ---
      let finalY = doc.lastAutoTable.finalY + 5;
      if (finalY > 230) { doc.addPage(); finalY = 20; }
      
      doc.rect(10, finalY, 110, 20);
      doc.setFontSize(8); doc.setFont('helvetica', 'bold');
      doc.text('RESUMO DO PERÍODO', 12, finalY + 4);
      doc.line(10, finalY + 6, 120, finalY + 6);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
      doc.text(`Dias Trab: ${totals.daysWorked ?? 0}`, 12, finalY + 10);
      doc.text(`Horas Trab: ${formatHHMM(totals.hoursWorked)}`, 12, finalY + 14);
      doc.text(`Extras 50%: ${formatHHMM(totals.overtimeDay)}`, 12, finalY + 18);
      doc.text(`Extras 100%: ${formatHHMM(totals.overtimeNight)}`, 65, finalY + 10);
      doc.text(`Add. Noturno: ${formatHHMM(totals.nightAdditionalHours)}`, 65, finalY + 14);
      doc.text(`Faltas: ${totals.absencesTotal ?? 0}`, 65, finalY + 18);

      doc.rect(125, finalY, 75, 20);
      doc.setFontSize(8); doc.setFont('helvetica', 'bold');
      doc.text('BANCO DE HORAS', 127, finalY + 4);
      doc.line(125, finalY + 6, 200, finalY + 6);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
      doc.text(`Saldo Anterior: ${formatHHMM(totals.bankPrevious)}`, 127, finalY + 10);
      doc.text(`Saldo do Mês: ${formatHHMM(totals.bankMonth)}`, 127, finalY + 14);
      doc.setFontSize(8); doc.setFont('helvetica', 'bold');
      doc.text(`Saldo Acumulado: ${formatHHMM(totals.bankAccumulated)}`, 127, finalY + 18);

      const sigY = finalY + 45;
      doc.line(20, sigY, 90, sigY); doc.line(120, sigY, 190, sigY);
      doc.setFontSize(8); doc.setFont('helvetica', 'bold');
      doc.text(emp.name || '-', 55, sigY + 4, { align: 'center' });
      doc.text(company.corporate_name || company.name || 'EMPRESA', 155, sigY + 4, { align: 'center' });
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6);
      doc.text('ASSINATURA DO COLABORADOR', 55, sigY + 7, { align: 'center' });
      doc.text('ASSINATURA DO EMPREGADOR', 155, sigY + 7, { align: 'center' });

      // Footer System (Logo e Texto lado a lado)
      const footerText = `Gerado em ${new Date().toLocaleString('pt-BR')} por ClockFy`;
      doc.setFontSize(6);
      const footerTextWidth = doc.getTextWidth(footerText);
      const footerGap = 2; // Espaço entre logo e texto
      
      let footerTotalWidth = footerTextWidth;
      let sysWidth = 0;
      let sysHeight = 0;

      if (systemLogo) {
        const sysMaxWidth = 18;
        const sysMaxHeight = 5;
        sysWidth = sysMaxWidth;
        sysHeight = sysMaxWidth / (systemLogo.ratio || 1);
        if (sysHeight > sysMaxHeight) {
          sysHeight = sysMaxHeight;
          sysWidth = sysMaxHeight * (systemLogo.ratio || 1);
        }
        footerTotalWidth += sysWidth + footerGap;
      }

      const footerStartX = 105 - (footerTotalWidth / 2);
      const footerY = 285; // Movido para o final da folha A4 (297mm)

      if (systemLogo) {
        try {
          // Desenha a logo
          doc.addImage(systemLogo.base64, 'PNG', footerStartX, footerY - (sysHeight / 2) - 0.5, sysWidth, sysHeight, undefined, 'FAST');
          // Desenha o texto ao lado
          doc.setTextColor(100);
          doc.text(footerText, footerStartX + sysWidth + footerGap, footerY, { align: 'left' });
        } catch (e) {
          doc.setTextColor(100);
          doc.text(footerText, 105, footerY, { align: 'center' });
        }
      } else {
        doc.setTextColor(100);
        doc.text(footerText, 105, footerY, { align: 'center' });
      }
      doc.setTextColor(0);

      doc.save(`espelho_ponto_${emp.name || 'colaborador'}_${start}_${end}.pdf`.replace(/\//g, '-'));
      toast.dismiss(toastId);
    } catch (err) {
      console.error('Error generating PDF:', err);
      toast.dismiss(toastId);
      toast.error('Erro ao gerar PDF.');
    }
  }

  useEffect(() => {
    if (!hasLoadedOnce) return;
    if (!selectedCompanyKey) {
      setTimesheetData([]);
      return;
    }

    const dp = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);
    const params = {};
    if (dp.startDate) params.startDate = dp.startDate;
    if (dp.endDate) params.endDate = dp.endDate;
    if (selectedServiceStation) params.serviceStationId = selectedServiceStation;
    if (selectedEmployee) params.employeeId = selectedEmployee;

    setTimesheetLoading(true);
    setTimesheetError('');

    Promise.allSettled(selectedCompanyIds.map((id) => Api.getTimesheet(id, params)))
      .then((results) => {
        const merged = results
          .filter((r) => r.status === 'fulfilled')
          .flatMap((r) => (Array.isArray(r.value?.data) ? r.value.data : []));

        const uniqueByEmployee = new Map();
        merged.forEach((row) => {
          const empId = row?.employee?.id;
          if (!empId) return;
          if (!uniqueByEmployee.has(String(empId))) uniqueByEmployee.set(String(empId), row);
        });

        setTimesheetData(Array.from(uniqueByEmployee.values()));
        setTimesheetHasLoadedOnce(true);
      })
      .catch((err) => {
        console.error(err);
        setTimesheetError('Erro ao carregar a folha de ponto. Tente novamente.');
        setTimesheetData([]);
        setTimesheetHasLoadedOnce(true);
      })
      .finally(() => setTimesheetLoading(false));
  }, [
    currentDate,
    hasLoadedOnce,
    periodMode,
    selectedCompanyKey,
    selectedEmployee,
    selectedServiceStation,
    selectedCompanyIds,
  ]);

  function handleCompanyFilterChange(newValues) {
    const allValue = 'all';
    const currentSelected = companyFilterValues;
    const isAllSelected = newValues.includes(allValue);
    const wasAllSelected = currentSelected.includes(allValue);

    let nextValues;

    if (isAllSelected && !wasAllSelected) {
      const allIds = companies.map((c) => String(c.id));
      nextValues = [allValue, ...allIds];
    } else if (!isAllSelected && wasAllSelected) {
      nextValues = [];
    } else if (wasAllSelected && newValues.length < currentSelected.length) {
      const remaining = newValues.filter((v) => v !== allValue);
      nextValues = remaining;
    } else {
      const specificIds = newValues.filter((v) => v !== allValue);
      const allIds = companies.map((c) => String(c.id));
      if (specificIds.length === allIds.length && allIds.length > 0) {
        nextValues = [allValue, ...specificIds];
      } else {
        nextValues = specificIds;
      }
    }

    if (nextValues.length === 0) {
      setCompanyFilterValues([allValue]);
      return;
    }

    setCompanyFilterValues(nextValues);
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container" ref={listRef}>
        {loading && !hasLoadedOnce && (
          (() => {
            const { CompanyManagerSkeleton } = Skeletons;
            return <CompanyManagerSkeleton />;
          })()
        )}

        {(hasLoadedOnce || !loading) && (
          <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiFileText size={22} />
                </div>
                <div>
                  <h1>Folha de Ponto</h1>
                  <p>Visualize e exporte o espelho de ponto dos colaboradores.</p>
                </div>
              </div>
              <div className="timesheet-header-actions">
                <DropdownButton label="Espelho de Ponto" variant="primary">
                  <button type="button" onClick={handleExportAllPDF}>
                    <FiFileText size={16} />
                    Completo - PDF
                  </button>
                  <button type="button" onClick={() => handleExport('mirror', null, 'espelho_de_ponto')}>
                    <FiFileText size={16} />
                    Completo - Excel
                  </button>
                  <button type="button" onClick={handleMirrorByCargo}>
                    <FiBriefcase size={16} />
                    Completo - Cargo
                  </button>
                  <button type="button" onClick={handleMirrorByPosto}>
                    <FiMapPin size={16} />
                    Completo - Posto
                  </button>
                </DropdownButton>

                <DropdownButton label="Ações">
                  <button type="button" onClick={() => handleExport('summary', null, 'folha_de_ponto')}>
                    <FiDownload size={16} />
                    Exportar Resumo
                  </button>
                  <button type="button" onClick={() => handleExport('audit', null, 'folha_de_ponto')}>
                    <FiTrendingUp size={16} />
                    Exportar Apuração
                  </button>
                  <button type="button" onClick={() => handleExport('detailed', null, 'folha_de_ponto')}>
                    <FiFileText size={16} />
                    Exportar Detalhado
                  </button>
                  <button type="button" onClick={() => handleExport('night_additional', null, 'folha_de_ponto')}>
                    <FiMoon size={16} />
                    Exportar Add. Noturno
                  </button>
                  <button type="button" onClick={() => handleExport('bank_hours', null, 'folha_de_ponto')}>
                    <FiClock size={16} />
                    Exportar Banco de Horas
                  </button>
                  <button type="button" onClick={() => handleExport('absences', null, 'folha_de_ponto')}>
                    <FiAlertTriangle size={16} />
                    Exportar Faltas
                  </button>

                  <button type="button" onClick={() => handleExportFiscal('afd_1510')}>
                    <FiFileText size={16} />
                    Gerar AFD - Portaria 1510/2009 (REP)
                  </button>
                  <button type="button" onClick={() => handleExportFiscal('afd_671')}>
                    <FiFileText size={16} />
                    Gerar AFD Portaria 671/2021 (REP-P)
                  </button>
                  <button type="button" onClick={() => handleExportFiscal('afdt')}>
                    <FiFileText size={16} />
                    Gerar AFDT
                  </button>
                  <button type="button" onClick={() => handleExportFiscal('acjef')}>
                    <FiFileText size={16} />
                    Gerar ACJEF
                  </button>
                </DropdownButton>
              </div>
            </div>

            <Card className="manager-filters-card">
              <div className="manager-filters-header">
                <div className="manager-filters-title">
                  <FiFilter size={16} />
                  <span>Filtros</span>
                </div>
              </div>

              <div className="manager-filters-grid-custom">
                <div className="manager-filter-group">
                  <DatePeriodFilter
                    periodMode={periodMode}
                    setPeriodMode={setPeriodMode}
                    currentDate={currentDate}
                    setCurrentDate={setCurrentDate}
                    customRange={customPeriod}
                    setCustomRange={setCustomPeriod}
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Empresa</label>
                  <MultiSelect
                    id="timesheet-company-filter"
                    options={companyOptions}
                    value={companyFilterValues}
                    onChange={handleCompanyFilterChange}
                    searchable
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Posto de Serviço</label>
                  <Select
                    value={selectedServiceStation}
                    onChange={(e) => setSelectedServiceStation(e.target.value)}
                    options={[
                      { value: '', label: 'TODOS' },
                      ...stationOptions,
                    ]}
                    searchable
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Colaborador</label>
                  <Select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    options={[
                      { value: '', label: 'TODOS' },
                      ...employeeOptions,
                    ]}
                    searchable
                  />
                </div>

                <div className="manager-filter-group">
                  <label className="manager-filter-label">Cargo</label>
                  <Select
                    value={selectedPosition}
                    onChange={(e) => setSelectedPosition(e.target.value)}
                    options={[
                      { value: '', label: 'TODOS' },
                      ...positionOptions,
                    ]}
                    searchable
                  />
                </div>
              </div>
            </Card>

            {timesheetError && (
              <div className="error" style={{ marginTop: 12 }}>
                {timesheetError}
              </div>
            )}

            {!timesheetError && timesheetHasLoadedOnce && !timesheetLoading && filteredTimesheetData.length === 0 && (
              <div className="empty-state" style={{ marginTop: 12 }}>
                <p>Nenhum colaborador encontrado para o período/filtros selecionados.</p>
              </div>
            )}

            {!timesheetError && (
              <Card className="timesheet-list-card">
                <div className="manager-filters-header" style={{ padding: '16px 16px 0 16px' }}>
                  <div className="manager-filters-title">
                    <FiFileText size={16} />
                    <span>Colaboradores</span>
                  </div>
                  {filteredTimesheetData.length > 0 && (
                    <span className="manager-filters-count">
                      {filteredTimesheetData.length} colaboradores
                    </span>
                  )}
                </div>

                <div className="timesheet-list">
                  {(timesheetLoading && !timesheetHasLoadedOnce) && (
                    <div className="timesheet-loading">Carregando...</div>
                  )}

                  {(!timesheetLoading || timesheetHasLoadedOnce) &&
                    filteredTimesheetData.slice(0, visibleCount).map((row) => {
                      const emp = row.employee || {};
                      const totals = row.totals || {};
                      const companyName = emp.company?.name || '';
                      const positionName = emp.position?.name || '';
                      const journeyName = emp.journey?.name || '';
                      const journeyType = emp.journey?.type || '';
                      const stationLabel =
                        Array.isArray(emp.serviceStations) && emp.serviceStations.length > 0
                          ? emp.serviceStations.map((s) => s.name).filter(Boolean).join(', ')
                          : '';

                      const bankPrevMeta = getBalanceMeta(totals.bankPrevious);
                      const bankMonthMeta = getBalanceMeta(totals.bankMonth);
                      const bankAccMeta = getBalanceMeta(totals.bankAccumulated);

                      const BankPrevIcon = bankPrevMeta.Icon;
                      const BankMonthIcon = bankMonthMeta.Icon;
                      const BankAccIcon = bankAccMeta.Icon;

                      return (
                        <div key={emp.id} className="timesheet-item">
                          <div className="timesheet-item-actions">
                            <TableActions>
                              <button
                                type="button"
                                onClick={() => handleExportPDF(row)}
                              >
                                <FiFileText size={16} />
                                Exportar (PDF)
                              </button>
                              <button
                                type="button"
                                onClick={() => handleExport('mirror', [row], `folha_de_ponto_${emp.id}`)}
                              >
                                <FiFileText size={16} />
                                Exportar (Excel)
                              </button>
                              <button
                                type="button"
                                onClick={() => handleExport('audit', [row], `folha_de_ponto_${emp.id}`)}
                              >
                                <FiTrendingUp size={16} />
                                Exportar Apuração
                              </button>
                              <button
                                type="button"
                                onClick={() => handleExport('summary', [row], `folha_de_ponto_${emp.id}`)}
                              >
                                <FiDownload size={16} />
                                Exportar Resumo
                              </button>
                            </TableActions>
                          </div>
                          <div className="timesheet-main">
                            <div className="timesheet-avatar">{getInitials(emp.name)}</div>
                            <div className="timesheet-info">
                              <div className="timesheet-name">{emp.name}</div>
                              <div className="timesheet-subtitle">{positionName || 'Sem cargo'}</div>

                              <div className="timesheet-meta">
                                <FiHash size={12} className="timesheet-meta-icon" />
                                <span>
                                  {emp.registration_number ? `Matrícula: ${emp.registration_number}` : 'Matrícula: -'}
                                </span>
                                <span className="timesheet-meta-sep">•</span>
                                <FiFileText size={12} className="timesheet-meta-icon" />
                                <span>{emp.id ? `ID: ${emp.id}` : 'ID: -'}</span>
                                <span className="timesheet-meta-sep">•</span>
                                <FiCalendar size={12} className="timesheet-meta-icon" />
                                <span>{emp.admission_date ? `Admissão: ${emp.admission_date}` : 'Admissão: -'}</span>
                              </div>

                              <div className="timesheet-meta">
                                <FiClock size={12} className="timesheet-meta-icon" />
                                <span>
                                  {journeyType || journeyName
                                    ? `${journeyType || ''}${journeyType && journeyName ? ' - ' : ''}${journeyName}`
                                    : 'Sem jornada'}
                                </span>
                              </div>

                              {stationLabel && (
                                <div className="timesheet-meta">
                                  <FiMapPin size={12} className="timesheet-meta-icon" />
                                  <span>{stationLabel}</span>
                                </div>
                              )}

                              {companyName && (
                                <div className="timesheet-meta">
                                  <FiBriefcase size={12} className="timesheet-meta-icon" />
                                  <span>{companyName}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="timesheet-metrics">
                            <div className="timesheet-metrics-col">
                              <div className="timesheet-metric timesheet-metric-strong">
                                <span className="timesheet-label">
                                  <FiCalendar size={13} className="timesheet-label-icon" />
                                  Dias Trabalhados
                                </span>
                                <span className="timesheet-value">{totals.daysWorked ?? 0}</span>
                              </div>
                              <div className="timesheet-metric">
                                <span className="timesheet-label">
                                  <FiClock size={13} className="timesheet-label-icon" />
                                  Hrs. Trabalhadas
                                </span>
                                <span className="timesheet-value">{formatHHMM(totals.hoursWorked)}</span>
                              </div>
                              <div className="timesheet-metric">
                                <span className="timesheet-label">
                                  <FiTrendingUp size={13} className="timesheet-label-icon" />
                                  Hrs. Projeção
                                </span>
                                <span className="timesheet-value">{formatHHMM(totals.projectedHours)}</span>
                              </div>
                              <div className="timesheet-metric">
                                <span className="timesheet-label">
                                  <FiZap size={13} className="timesheet-label-icon" />
                                  HE. Diurno
                                </span>
                                <span className="timesheet-value">{formatHHMM(totals.overtimeDay)}</span>
                              </div>
                            </div>

                            <div className="timesheet-metrics-col">
                              <div className="timesheet-metric">
                                <span className="timesheet-label">
                                  <FiMoon size={13} className="timesheet-label-icon" />
                                  HE. Noturno
                                </span>
                                <span className="timesheet-value">{formatHHMM(totals.overtimeNight)}</span>
                              </div>
                              <div className="timesheet-metric">
                                <span className="timesheet-label">
                                  <FiClock size={13} className="timesheet-label-icon" />
                                  Hrs. Intra
                                </span>
                                <span className="timesheet-value">{formatHHMM(totals.hoursIntra)}</span>
                              </div>
                              <div className="timesheet-metric">
                                <span className="timesheet-label">
                                  <FiCalendar size={13} className="timesheet-label-icon" />
                                  Hrs. Escala
                                </span>
                                <span className="timesheet-value">{formatHHMM(totals.hoursScale)}</span>
                              </div>
                              <div className="timesheet-metric">
                                <span className="timesheet-label">
                                  <FiSun size={13} className="timesheet-label-icon" />
                                  Add. Noturno
                                </span>
                                <span className="timesheet-value">{formatHHMM(totals.nightAdditionalHours)}</span>
                              </div>
                            </div>

                            <div className="timesheet-metrics-col">
                              <div className="timesheet-metric">
                                <span className="timesheet-label">
                                  <FiFileText size={13} className="timesheet-label-icon" />
                                  Saldo Anterior
                                </span>
                                <span className={`timesheet-value ${bankPrevMeta.className}`}>
                                  {BankPrevIcon && <BankPrevIcon size={14} />}
                                  {formatHHMM(totals.bankPrevious)}
                                </span>
                              </div>
                              <div className="timesheet-metric">
                                <span className="timesheet-label">
                                  <FiTrendingUp size={13} className="timesheet-label-icon" />
                                  Saldo Mês
                                </span>
                                <span className={`timesheet-value ${bankMonthMeta.className}`}>
                                  {BankMonthIcon && <BankMonthIcon size={14} />}
                                  {formatHHMM(totals.bankMonth)}
                                </span>
                              </div>
                              <div className="timesheet-metric">
                                <span className="timesheet-label">
                                  <FiTrendingUp size={13} className="timesheet-label-icon" />
                                  Acumulado
                                </span>
                                <span className={`timesheet-value ${bankAccMeta.className}`}>
                                  {BankAccIcon && <BankAccIcon size={14} />}
                                  {formatHHMM(totals.bankAccumulated)}
                                </span>
                              </div>
                              <div className="timesheet-metric">
                                <span className="timesheet-label">
                                  <FiAlertTriangle size={13} className="timesheet-label-icon" />
                                  Faltas
                                </span>
                                <span className="timesheet-value">{totals.absencesTotal ?? 0}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  
                  {visibleCount < filteredTimesheetData.length && (
                    <div className="timesheet-loading" style={{ textAlign: 'center' }}>
                      Carregando mais colaboradores...
                    </div>
                  )}
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </PageComponent>
  );
}
